/**
 * Admin · compliance & audit (GET /admin/compliance, POST /admin/dsr/{id}/resolve,
 * GET /admin/compliance/bias-export → CSV download).
 * Consent ledger by purpose (active state + withdrawals candidates made themselves), DSR queue
 * with human resolution, exportable bias audit.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { getToken } from "../../lib/auth.js";
import { Alert, Info } from "../../components/icons.js";
import { Modal, apiErrorText } from "./adminUi.js";

interface CompliancePayload {
  /** `active` is the LATEST ledger row per (persona, purpose) still in force — not every grant
   *  ever written, which would keep counting purposes candidates have since withdrawn. */
  consentSummary: { purpose: string; active: number; withdrawn: number }[];
  recentConsents: { purpose: string; state: string; ts: string }[];
  dsrQueue: { id: string; personaId: string; kind: "export" | "erase"; status: string; createdAt: string }[];
}

interface ComplianceResponse {
  compliance: CompliancePayload;
}

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

const DSR_STATUS_PILL: Record<string, string> = {
  received: "pill r",
  processing: "pill pv",
  done: "pill v",
  rejected: "pill n",
};

export function Compliance(): ReactNode {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-compliance"],
    queryFn: () => api.get<ComplianceResponse>("/admin/compliance"),
  });
  const c = data?.compliance;

  const activeTotal = c ? c.consentSummary.reduce((s, x) => s + x.active, 0) : 0;
  const withdrawnTotal = c ? c.consentSummary.reduce((s, x) => s + x.withdrawn, 0) : 0;
  const openDsr = c ? c.dsrQueue.filter((d) => d.status === "received" || d.status === "processing").length : 0;
  const erasuresDone = c ? c.dsrQueue.filter((d) => d.kind === "erase" && d.status === "done").length : 0;

  // --- DSR resolution (erase = crit confirm modal) ---
  const [confirmErase, setConfirmErase] = useState<{ id: string; personaId: string } | null>(null);
  const resolve = useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/admin/dsr/${id}/resolve`),
    onSuccess: () => {
      setConfirmErase(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-compliance"] });
    },
  });

  // --- bias-audit CSV export (raw fetch: the endpoint returns text/csv, not JSON) ---
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const downloadBiasExport = async (): Promise<void> => {
    setExporting(true);
    setExportError(false);
    try {
      const token = getToken();
      const res = await fetch(`${BASE_URL}/admin/compliance/bias-export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`export failed: ${res.status}`);
      const csv = await res.text();
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "talenthop-bias-audit.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Compliance & audit</h1>
          <div className="sub">Cross-tenant, auditable, exportable — compliance is a product surface, not a PDF</div>
        </div>
        <button className="btn" disabled={exporting} onClick={() => void downloadBiasExport()}>
          {exporting ? "Exporting…" : "Export bias audit (CSV)"}
        </button>
      </div>

      {exportError && (
        <div className="banner crit" style={{ marginBottom: 16 }}>
          <Alert />
          <span>Couldn't export the bias audit. Please retry in a moment.</span>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading compliance ledger…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load compliance data. Please retry in a moment.</span>
        </div>
      )}

      {c && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Consent grants · active</div>
              <div className="n tnum">{activeTotal.toLocaleString("en-IN")}</div>
              <div className="d">still in force · latest row per purpose</div>
            </div>
            <div className="card pad stat">
              <div className="l">Withdrawals honoured</div>
              <div className="n good tnum">{withdrawnTotal.toLocaleString("en-IN")}</div>
              <div className="d">candidate-initiated · capture stops on the next check</div>
            </div>
            <div className="card pad stat">
              <div className="l">DSR queue · open</div>
              <div className="n tnum">{openDsr}</div>
              <div className="d">export + erase requests</div>
            </div>
            <div className="card pad stat">
              <div className="l">Erasures completed</div>
              <div className="n tnum">{erasuresDone}</div>
              <div className="d">recordings + PII deleted · per-surface log</div>
            </div>
          </div>

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Consent ledger · by purpose</h3>
                <span className="tag">Per purpose</span>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Purpose</th>
                      {/* Active = in force now; Withdrawn = acts of withdrawal, not a remainder.
                          They do not sum to a total, and labelling the first "Granted" invited
                          exactly that misreading. */}
                      <th>Active</th>
                      <th>Withdrawn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.consentSummary.length === 0 && (
                      <tr>
                        <td colSpan={3} className="mut sm">
                          No consent rows yet.
                        </td>
                      </tr>
                    )}
                    {c.consentSummary.map((s) => (
                      <tr key={s.purpose}>
                        <td style={{ textTransform: "capitalize" }}>{s.purpose.replace(/_/g, " ")}</td>
                        <td className="tnum">{s.active.toLocaleString("en-IN")}</td>
                        <td className="tnum">{s.withdrawn.toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Recent consent events</h3>
                <span className="tag">Live</span>
              </div>
              <div className="card-b">
                {c.recentConsents.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    Nothing yet.
                  </p>
                )}
                {/* Grants and withdrawals share this feed; `ts` is already the event's own time
                    (withdrawn_at for a withdrawal), so a withdrawal is legible as an event with a
                    date rather than an unexplained second row for the same purpose. */}
                {c.recentConsents.map((r, i) => (
                  <div key={`${r.ts}-${i}`} className="krow">
                    <span className="k" style={{ textTransform: "capitalize" }}>
                      {r.purpose.replace(/_/g, " ")}
                    </span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="mut sm">{new Date(r.ts).toLocaleString()}</span>
                      <span className={r.state === "granted" ? "pill v" : "pill r"}>{r.state}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Data-subject requests</h3>
              <span className="mut sm">Erasure = secure delete · irreversible · human-confirmed</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Persona</th>
                    <th>Kind</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {c.dsrQueue.length === 0 && (
                    <tr>
                      <td colSpan={5} className="mut sm">
                        The DSR queue is empty.
                      </td>
                    </tr>
                  )}
                  {c.dsrQueue.map((d) => (
                    <tr key={d.id}>
                      <td className="mut sm">{new Date(d.createdAt).toLocaleString()}</td>
                      <td style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>{d.personaId.slice(0, 8)}…</td>
                      <td>
                        <span className={d.kind === "erase" ? "pill r" : "pill n"}>{d.kind}</span>
                      </td>
                      <td>
                        <span className={DSR_STATUS_PILL[d.status] ?? "pill n"}>{d.status}</span>
                      </td>
                      <td>
                        {d.status === "done" || d.status === "rejected" ? (
                          <span className="mut sm">resolved</span>
                        ) : d.kind === "erase" ? (
                          <button
                            className="btn sm"
                            style={{ color: "var(--crit)", borderColor: "var(--crit)" }}
                            disabled={resolve.isPending}
                            onClick={() => setConfirmErase({ id: d.id, personaId: d.personaId })}
                          >
                            Erase (secure delete)
                          </button>
                        ) : (
                          <button
                            className="btn sm ghost"
                            disabled={resolve.isPending}
                            onClick={() => resolve.mutate(d.id)}
                          >
                            {resolve.isPending ? "Resolving…" : "Mark exported"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {resolve.isError && (
            <div className="banner crit" style={{ marginBottom: 16 }}>
              <span>{apiErrorText(resolve.error, "Couldn't resolve the request. Please retry.")}</span>
            </div>
          )}

          <div className="banner info" style={{ marginBottom: 16 }}>
            <Info />
            <div>
              The consent ledger is <b>append-only and per-purpose</b>; Aadhaar is stored as an outcome only, never
              the number. A candidate withdraws from their own interview link — that appends a <code>withdrawn</code>{" "}
              row rather than editing the grant, so both halves of "consented on the 3rd, withdrew on the 9th" stay
              provable, and capture re-reads the ledger before it records. Withdrawal stops processing; it is not
              erasure. Erasure physically deletes the recordings and résumés and blanks every PII field, writing a
              per-surface log as proof — thorough and irreversible.
            </div>
          </div>
        </>
      )}

      {/* Precise, because this is the page an auditor reads. "A human always makes the final hiring
          decision" was not true of advancement: the worker writes decision_records with
          action='advance', provenance='auto' after the resume screen and again after scoring. What IS
          true — and is what matters under DPDP/EU AI Act automated-decision rules — is that no
          candidate is ever cut by the engine. Say that, and say who proposes the advance. */}
      <div className="disclaimer">
        <b>Rejection is always human — the engine never cuts a candidate.</b> Advancement is a ranking the engine
        proposes and a person confirms: engine-proposed advances are stamped <code>provenance='auto'</code> in the
        decision ledger, and every human decision (advance, proposed cut, override) is stamped{" "}
        <code>'human'</code> with the actor. Bias-audit exports are aggregate only (band distribution +
        calibration) — no per-candidate rows leave the platform.
      </div>

      {confirmErase && (
        <Modal title="Erase this persona's data?" onClose={() => setConfirmErase(null)}>
          <div className="banner crit" style={{ marginBottom: 12 }}>
            <Alert />
            <div>
              This permanently deletes the recordings and résumés and blanks every PII field for persona{" "}
              <code style={{ fontFamily: "var(--mono)" }}>{confirmErase.personaId.slice(0, 8)}…</code>. There is
              <b> no undo</b>.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost" onClick={() => setConfirmErase(null)}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate(confirmErase.id)}
            >
              {resolve.isPending ? "Erasing…" : "Erase permanently"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
